import {openDialog, Separator} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactElement, useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import {isNil, isNotNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {useLazyGetCustomerV2Query} from '@dms/api/customer';
import {
  GetDirectSaleResponse,
  GetWarehousesResponse,
  useBulkDeleteDirectSaleItemsMutation,
  useBulkReturnDirectSaleSparePartsMutation,
  useLazyGetDirectSaleQuery,
  usePatchDirectSaleGetCurrentPriceMutation,
  usePatchPartsRequestCancelByOriginEntityMutation,
  usePostIssueNotesMutation,
  usePostPartsRequestMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  getNaturalPersonFullName,
  handleApiError,
} from '@dms/shared';
import {isNonEditableDirectSaleState, WAREHOUSE_DIALOG_IDS} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import type {
  ActionCallback,
  EagGridMaximization,
  GridDataChange,
  QueryFilterObject,
} from 'features/eag-grid';

import {BulkAssignMechanic} from './BulkAssignMechanic';
import {useDirectSaleItemsBulkActions} from './hooks/useDirectSaleItemsBulkActions';
import {useDirectSaleItemsBulkSelection} from './hooks/useDirectSaleItemsBulkSelection';
import {RequestTabEdit, RequestTabEditProps} from './RequestTabEdit';
import {RequestTabLabourModal} from './RequestTabLabourModal';
import {RequestTabMaterialModal} from './RequestTabMaterialModal';
import {RequestTabSegmentActionsRow} from './RequestTabSegmentActionsRow';
import {WorkItemsSegmentActions} from './WorkItemsSegmentActions';

// Only the columns host-side code actually reads from the rows (the action callback's
// rowData and the bulk-selection helpers). Except for `id` (always present on every
// row), each one must also be listed in PREDEFINED_COLUMNS so it is fetched even when
// the user hides the column.
type DirectSaleItemRowData = {
  id: string;
  unitPrice?: {value: {amount: number; currency: string}};
  amount?: {value: number};
  neededAt?: {value: string};
  articleId?: {value: string};
  warehouseId?: {value: string};
  type?: {value: {key: string; label: string}};
};

interface WorkItemsSegmentProps extends RequiredTestIdProps {
  maximization?: EagGridMaximization;
  /** The request tab segment control; shares one row with the work-item actions. */
  segment: ReactElement;
  /** False on the other segments: the component then renders only the segment row. */
  isGridVisible: boolean;
  directSale: GetDirectSaleResponse | Nullish;
  isEditingDisabled: boolean;
  availableWarehouses: GetWarehousesResponse;
  onMaterialReturnModalOpen: VoidFunction;
}

/**
 * The work items of a direct sale: the segment row with the work-item actions and the items
 * grid. It stays mounted on every segment of the request tab, so the segment keeps its place
 * in the tree and its keyboard focus. The maximized grid repeats the row above itself, because
 * the overlay covers the original one.
 */
export function WorkItemsSegment(props: WorkItemsSegmentProps) {
  const {directSaleId} = useWarehouseParams();

  const dispatch = useDispatch();

  const [isLabourModalVisible, openLabourModal, closeLabourModal] = useBoolean();
  const [isMaterialModalVisible, openMaterialModal, closeMaterialModal] = useBoolean();

  const [editDialogProps, setEditDialogProps] = useState<RequestTabEditProps | null>(null);
  const [gridData, setGridData] = useState<GridDataChange | null>(null);
  const itemsSelection = useDirectSaleItemsBulkSelection(
    WAREHOUSE_GRID_CODES.warehouseDirectSaleItem,
    gridData
  );
  const itemsActions = useDirectSaleItemsBulkActions({
    gridData,
    directSaleId,
    directSale: props.directSale,
  });

  // Grid data of an unmounted grid is stale; the grid reports it again when it mounts.
  if (!props.isGridVisible && isNotNil(gridData)) {
    setGridData(null);
  }

  const [getDirectSaleDetail] = useLazyGetDirectSaleQuery();
  const [getCustomer] = useLazyGetCustomerV2Query();
  const [createIssueNote] = usePostIssueNotesMutation();
  const [postPartsRequest] = usePostPartsRequestMutation();
  const [deleteDirectSalesItems] = useBulkDeleteDirectSaleItemsMutation();
  const [patchDirectSaleGetCurrentPrice] = usePatchDirectSaleGetCurrentPriceMutation();
  const [cancelPartsRequest] = usePatchPartsRequestCancelByOriginEntityMutation();
  const [bulkReturnSpareParts] = useBulkReturnDirectSaleSparePartsMutation();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {directSaleId}]),
    [directSaleId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    rowData,
    refreshData,
    queryId,
    deselectAll,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;
    const directSaleItems = (isArray(rowData) ? rowData : [rowData]) as DirectSaleItemRowData[];

    await match(actionKey)
      .with('getCurrentPrices', async () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        await patchDirectSaleGetCurrentPrice({
          directSaleId,
          body: {directSaleItemId: ids},
        })
          .unwrap()
          .then(() => refreshData())
          .then(deselectAll)
          // Updated item prices change the direct sale totals shown outside the grid.
          .then(() => dispatch(metadaApi.util.invalidateTags(['directSales'])))
          .catch(handleApiError);
      })
      .with('redirectDetail', 'edit', async () => {
        const directSaleDetail = getDirectSaleDetail({directSaleId}).unwrap().catch(handleApiError);

        await directSaleDetail.then((data) => {
          const isEditingDisabled = isNonEditableDirectSaleState(data?.state);

          const handleClose = () => {
            setEditDialogProps(null);
            refreshData();
            // Invalidating manually onClose to keep dialog rowControl saving feature intact.
            dispatch(metadaApi.util.invalidateTags(['directSales']));
          };

          setEditDialogProps({
            itemId: id,
            isEditingDisabled,
            dataQueryId: queryId,
            directSaleId,
            authorizationProfileId: data?.authorizationProfileId,
            onClose: handleClose,
            'data-testid': props['data-testid'] as string,
          });
        });
      })
      .with('delete', async () => {
        const itemsIds = directSaleItems.map((directSaleItem) => ({id: directSaleItem.id}));

        await deleteDirectSalesItems({
          directSaleId,
          body: {
            items: itemsIds,
          },
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('issue', async () => {
        const directSaleNumber = props.directSale?.number;
        const directSaleAuthorizationProfileId = props.directSale?.authorizationProfileId;

        if (isNil(directSaleNumber)) {
          throw new Error('directSaleNumber is not defined');
        }

        if (isNil(directSaleAuthorizationProfileId)) {
          throw new Error('directSaleAuthorizationProfileId is not defined');
        }

        const customerDataPromise = isNotNil(props.directSale?.customerId)
          ? getCustomer({customerId: props.directSale?.customerId ?? ''})
              .unwrap()
              .catch(handleApiError)
          : Promise.resolve(null);
        await customerDataPromise.then(async (customerData) => {
          await createIssueNote({
            directSaleId,
            body: {
              originDocId: directSaleId,
              originDocType: 'direct-sale',
              originDocNumber: directSaleNumber,
              authorizationProfileId: directSaleAuthorizationProfileId,
              customerName: getNaturalPersonFullName(customerData?.foundingPerson) ?? undefined,
              items: directSaleItems.map((directSaleItem) => ({
                warehouseId: directSaleItem.warehouseId?.value ?? '',
                originItemId: directSaleItem.id,
                articleId: directSaleItem.articleId?.value as string,
                requestItemOriginEntityType: 'wrh_sale_item',
              })),
            },
          })
            .unwrap()
            .then(() => refreshData())
            .catch(handleApiError);
        });
      })
      .with('requestCancellation', async () => {
        const originEntityIds = directSaleItems.map((directSaleItem) => directSaleItem.id);

        await cancelPartsRequest({
          directSaleId,
          body: {
            originEntityId: originEntityIds,
          },
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('request', async () => {
        await postPartsRequest({
          directSaleId,
          body: {
            originEntityType: 'wrh_sale_item',
            requestItems: directSaleItems.map((directSaleItem) => ({
              originEntityId: directSaleItem.id,
              articleId: directSaleItem.articleId?.value as string,
              originEntityHeaderType: 'direct-sale',
              originEntityHeaderId: directSaleId,
              itemNeededAtDate: directSaleItem?.neededAt?.value,
              proposedSalePrice: directSaleItem.unitPrice?.value?.amount,
              quantity: directSaleItem.amount?.value ?? 0,
            })),
          },
        })
          .unwrap()
          .then(() => refreshData())
          .then(deselectAll)
          .catch(handleApiError);
      })

      .with('return', async () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        await bulkReturnSpareParts({directSaleId, body: {directSaleItemId: ids}})
          .unwrap()
          .then(deselectAll)
          .then(props.onMaterialReturnModalOpen)
          .catch(handleApiError);
      })
      .with('assignMechanic', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        const authorizationProfileId = props.directSale?.authorizationProfileId;

        if (isNil(authorizationProfileId)) {
          throw new Error('Cannot assign mechanic. The authorization profile id is missing.');
        }

        openDialog(
          <BulkAssignMechanic
            directSaleId={directSaleId}
            authorizationProfileId={authorizationProfileId}
            rowIds={ids}
            onSuccess={() => {
              refreshData();
              deselectAll();
            }}
            data-testid={suffixTestId('dialogs.assignMechanic', props)}
          />,
          {
            id: WAREHOUSE_DIALOG_IDS.bulkAssignMechanicDialog,
            title: i18n.t('entity.warehouse.labels.assignMechanic'),
            size: 'small',
            scrollBehavior: 'outside',
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const renderActions = (isCompact: boolean) => (
    <Show when={props.isGridVisible}>
      <WorkItemsSegmentActions
        isCompact={isCompact}
        isEditingDisabled={props.isEditingDisabled}
        itemsSelection={itemsSelection}
        itemsActions={itemsActions}
        onAddLabour={openLabourModal}
        onAddMaterial={openMaterialModal}
        data-testid={props['data-testid']}
      />
    </Show>
  );

  const row = <RequestTabSegmentActionsRow segment={props.segment} renderActions={renderActions} />;

  return (
    <VStack height={props.isGridVisible ? '100%' : undefined}>
      <RequestTabEdit {...(editDialogProps as RequestTabEditProps)} />

      {row}

      <Show when={props.isGridVisible}>
        <Separator />

        <DmsDataGrid
          maximization={props.maximization}
          maximizedSectionHeader={row}
          gridCode={WAREHOUSE_GRID_CODES.warehouseDirectSaleItem}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          queryModifier={queryModifier}
          onGridDataChange={setGridData}
          emptyState={{
            headline: i18n.t('entity.warehouse.notifications.noWorkItems'),
            subheadline: i18n.t('entity.warehouse.notifications.addWorkItems'),
          }}
          data-testid="requestedItems"
        />
      </Show>

      <Show when={isLabourModalVisible}>
        <RequestTabLabourModal
          directSaleId={directSaleId}
          authorizationProfileId={props.directSale?.authorizationProfileId}
          onDiscard={closeLabourModal}
          data-testid={testIds.warehouse.directSalesDetailOverview('dialogs.addLabour')}
        />
      </Show>

      <Show when={isMaterialModalVisible}>
        <RequestTabMaterialModal
          directSaleId={directSaleId}
          availableWarehouses={props.availableWarehouses}
          authorizationProfileId={props.directSale?.authorizationProfileId}
          onDiscard={closeMaterialModal}
          data-testid={testIds.warehouse.directSalesDetailOverview('dialogs.addMaterial')}
        />
      </Show>
    </VStack>
  );
}

const PREDEFINED_COLUMNS = [
  'articleId',
  'warehouseId',
  'neededAt',
  'unitPrice',
  'amount',
  'type',
] as const satisfies readonly (keyof DirectSaleItemRowData)[];
